import { LucideMoveRight } from 'lucide-react';
import type React from 'react';
import { Progress } from '@/components/shadcn/progress.tsx';
import type { StepItem } from '../hooks/wizard-helpers';
import type { ValidationChange } from '../types/base-entity';
import FormActions from './form-actions.tsx';
import FormSidebar from './form-sidebar.tsx';

export type WizardLayoutProps = {
  steps: StepItem[];
  currentStep: number;
  selectedAreas: string[];
  breadcrumbGroup: string;
  progressPercentage: number;
  activeStep: StepItem;
  isValid: boolean | undefined;
  canSubmitDiscovery: boolean;
  submitting: boolean;
  onStepClick: (index: number) => void;
  onBack: () => void;
  onSaveExit: () => void;
  onNext: () => void;
  onValidationChange: (valid: boolean) => void;
  onEdit: (sectionKey: string) => void;
};

export function WizardLayout({
  steps,
  currentStep,
  selectedAreas,
  breadcrumbGroup,
  progressPercentage,
  activeStep,
  isValid,
  canSubmitDiscovery,
  submitting,
  onStepClick,
  onBack,
  onSaveExit,
  onNext,
  onValidationChange,
  onEdit,
}: WizardLayoutProps) {
  const ActiveComponent = activeStep.Component as React.ComponentType<ValidationChange & { onEdit?: (sectionKey: string) => void }>;

  return (
    <div className='flex h-screen bg-zinc-50 overflow-hidden'>
      <FormSidebar steps={steps} currentStep={currentStep} selectedAreas={selectedAreas} onStepClick={onStepClick} />

      <div className='flex-1 flex flex-col overflow-hidden'>
        <div className='flex-1 overflow-y-auto flex flex-col  lg:items-center'>
          <div className='w-full max-w-5xl px-6 lg:flex lg:flex-col lg:items-center lg:min-h-0'>
            {breadcrumbGroup !== 'Review' ? (
              <div className='flex flex-col w-full px-4'>
                <div>
                  <div className='flex justify-between items-center md:hidden'>
                    <div className='text-sm font-semibold text-gray-900'>
                      Section {currentStep + 1} of {steps.length}
                    </div>
                    <div className='text-sm text-gray-500'>{`${progressPercentage}%`}</div>
                  </div>
                  <div className='md:hidden mt-2'>
                    <Progress value={progressPercentage} className='rounded-full [&>div]:rounded-full' />
                  </div>
                  {activeStep.key !== 'review' && (
                    <nav className='flex items-center mt-6 text-sm' aria-label='Breadcrumb'>
                      <div className='text-gray-500'>{breadcrumbGroup}</div>
                      <LucideMoveRight className='w-4 h-3 text-gray-500 mx-3' aria-hidden />
                      <div className='font-medium tracking-tight text-gray-900'>{activeStep.title}</div>
                    </nav>
                  )}
                </div>
                <div className='h-px bg-gray-300 mt-4' />
              </div>
            ) : null}
            <div className='w-full px-4 lg:flex lg:justify-center'>
              <ActiveComponent onValidationChange={onValidationChange} {...(activeStep.key === 'review' ? { onEdit } : {})} />
            </div>
          </div>
        </div>

        <FormActions
          onBack={onBack}
          onSaveExit={onSaveExit}
          nextButtonLabel={activeStep.key === 'review' ? (submitting ? 'Submitting…' : 'Submit Discovery') : 'Next'}
          onNext={onNext}
          disableBack={currentStep === 0}
          disableNext={activeStep.key === 'review' ? !canSubmitDiscovery || !isValid || submitting : !isValid}
        />
      </div>
    </div>
  );
}
